import { Chip, IconButton, Paper, Skeleton, Typography } from "@mui/material";
import { Box, Grid, Stack } from "@mui/system";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import TrendsBDAds from "./TrendsBreakdownBars/TrendsBDAds";
import TrendsBDCountry from "./TrendsBreakdownBars/TrendsBDCountry";
import TrendsBDTrafficSource from "./TrendsBreakdownBars/TrendsBDTrafficSource";
import TrendsBDKwMtTable from "./TrendsBreakdownTables/TrendsBDKwMtTable";
import TrendsBDBrandsTable from "./TrendsBreakdownTables/TrendsBDBrandsTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useDispatch } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TrendsDashboardEndPointLogic } from "../../../../../logic/AssetManagement/Trends/TrendsDashboardEndPointLogic";
import store from "../../../../../store";
import {
  setBrandsDataTrendsBreakdown,
  // setCampaignDataTrendsBreakdown,
  setCountryDataTrendsBreakdown,
  setKeywordMatchTypeDataTrendsBreakdown,
  setOrganizationDimensions,
  setTrafficSourceDataTrendsBreakdown,
} from "../../../../../features/trendsSlice";
import { trendsService } from "../../../../../services/api/trends.endpoints";
import {
  TrendsBreakdownQueryIds,
  TrendsDataSetQueryIds,
} from "../helpers/funcs/trendsAllQueryIDs";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import timeUnitParamMap from "../helpers/funcs/timeUnitParamMap";
import FloatingActionItems from "../../../../../pages/FloatingItems/FloatingActionItems";
import widgetMetricMap, {
  funnelRatioMetricMap,
  profitAndFirstTimeDepositMetricMap,
} from "../helpers/funcs/widgetMetricMap";
import dayjs from "dayjs";

const TrendsBreakdown = () => {
  const { userData } = useSelector((state: any) => state.user);
  const { selectedStartDate, selectedEndDate } = useSelector(
    (state: any) => state.trends
  );
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const pointDate = searchParams.get("point_date") || ""; // e.g., "22-09-2024"
  // const pointValue = searchParams.get("values_of_point_date"); // e.g., "320"
  const widgetName = searchParams.get("widget_name"); // e.g., "CAC"
  const fullWidgetName: any = searchParams.get("full_widget_name");
  const metricName: any = searchParams.get("metric_name");
  const valueSuffixParams = searchParams.get("value_suffix");
  const selectedSiteParam = searchParams.get("selectedSite") || "";
  const dimensionParam = searchParams.get("dimension") || "";
  const selectedSiteIds = selectedSiteParam?.split(",") || [];
  const fetchBy = searchParams.get("fetch_by") || "";

  const [isPointDateLoading, setIsPointDateLoading] = useState(false);
  const [
    trendsOverviewDataforMetricValue,
    setTrendsOverviewDataforMetricValue,
  ] = useState([]) as any;
  const previousPointDate = useRef<string>("");
  const [day, month, year] = pointDate.split("-");
  const formattedDate = `${year}-${month}-${day}`;
  const navigate = useNavigate();
  const matchingSites = selectedSiteIds.map((siteId) => {
    const match = userData?.data?.website_permissions?.find(
      (entry: any) => Object.keys(entry)[0] === siteId
    );
    if (match) {
      const key = Object.keys(match)[0];
      return match[key]?.ui_site_name || siteId;
    }
    return siteId;
  });
  const metricKey =
    fullWidgetName === "Funnel Ratio"
      ? funnelRatioMetricMap[metricName] || ""
      : fullWidgetName === "Profit and First Time Deposit"
      ? profitAndFirstTimeDepositMetricMap[metricName] || ""
      : widgetMetricMap[fullWidgetName];

  const dimensions = dimensionParam?.split(",") || [];
  const getTimeUnit = () => {
    if (!fullWidgetName) return "day";
    const timeUnitKey = timeUnitParamMap[fullWidgetName] || "timeUnit";
    return searchParams.get(timeUnitKey) || "day";
  };
  const time_unit = getTimeUnit();
  const { minDate, maxDate, availableDates } = useMemo(() => {
    if (!trendsOverviewDataforMetricValue?.query_output?.length) {
      return { minDate: null, maxDate: null, availableDates: [] };
    }

    const dates = trendsOverviewDataforMetricValue.query_output
      .map((item: any) => item.time_unit)
      .filter(Boolean)
      .sort(); // Sort dates in ascending order

    return {
      minDate: dates[0] || null,
      maxDate: dates[dates.length - 1] || null,
      availableDates: dates,
    };
  }, [trendsOverviewDataforMetricValue]);

  // Convert point date to YYYY-MM-DD format for comparison
  const formattedPointDateForComparison = useMemo(() => {
    if (!pointDate) return "";
    const [d, m, y] = pointDate.split("-");
    return `${y}-${m}-${d}`;
  }, [pointDate]);

  // Check if navigation buttons should be disabled
  const navigationLimits = useMemo(() => {
    const currentFormattedDate = formattedPointDateForComparison;

    return {
      isPreviousDisabled:
        !currentFormattedDate || !minDate || currentFormattedDate <= minDate,
      isNextDisabled:
        !currentFormattedDate || !maxDate || currentFormattedDate >= maxDate,
    };
  }, [formattedPointDateForComparison, minDate, maxDate]);

  // Helper to shift the date by days
  const changePointDate = (direction: "previous" | "next") => {
    if (!pointDate) return;
    if (direction === "previous" && navigationLimits.isPreviousDisabled) return;
    if (direction === "next" && navigationLimits.isNextDisabled) return;
    const [day, month, year] = pointDate.split("-");
    const currentDate = new Date(`${year}-${month}-${day}`);
    const offset = direction === "previous" ? -1 : 1;

    const timeUnit = time_unit.toLowerCase(); // "day", "week", "month"

    if (timeUnit === "day") {
      currentDate.setDate(currentDate.getDate() + offset);
    } else if (timeUnit === "week") {
      currentDate.setDate(currentDate.getDate() + offset * 7);
    } else if (timeUnit === "month") {
      currentDate.setMonth(currentDate.getMonth() + offset);
    }

    const newDay = String(currentDate.getDate()).padStart(2, "0");
    const newMonth = String(currentDate.getMonth() + 1).padStart(2, "0");
    const newYear = currentDate.getFullYear();

    const newDateStr = `${newDay}-${newMonth}-${newYear}`;
    const newFormattedDate = `${newYear}-${newMonth}-${newDay}`;
    if (!availableDates.includes(newFormattedDate)) {
      // If the exact date is not available, find the closest available date
      const sortedDates = [...availableDates].sort();
      let closestDate = null;

      if (direction === "next") {
        closestDate = sortedDates.find(
          (date) => date > formattedPointDateForComparison
        );
      } else {
        closestDate = sortedDates
          .reverse()
          .find((date) => date < formattedPointDateForComparison);
      }

      if (closestDate) {
        const [closestYear, closestMonth, closestDay] = closestDate.split("-");
        const closestDateStr = `${closestDay}-${closestMonth}-${closestYear}`;

        const updatedSearchParams = new URLSearchParams(
          searchParams.toString()
        );
        updatedSearchParams.set("point_date", closestDateStr);
        navigate({ search: updatedSearchParams.toString() });
        return;
      }
    }

    const updatedSearchParams = new URLSearchParams(searchParams.toString());
    updatedSearchParams.set("point_date", newDateStr);
    navigate({ search: updatedSearchParams.toString() });
  };

  const showBrandsTable = useMemo(() => {
    const fullWidgetList = [
      "Click Out",
      "Earn Per Click",
      "cost per acquisition",
      "cost per acquisition player value",
      "Average Cost Per Acquisition Deal",
      "First Time Deposit",
      "Gap Between First Time Deposit and Qualified First Time Deposit",
      "Player Value",
      "Net Revenue Per First-Time Depositor",
      "Total Income",
      "Revenue Share",
      "Revenue Share Player Value",
      "Average Revenue Share Percentage Deal",
    ]; // add more as needed
    const metricNameList = ["fr-regs-out", "fr-ftd-regs", "fr-qftd-ftd"]; // add more as needed

    if (metricName) {
      return metricNameList.includes(metricName);
    }
    return fullWidgetName ? fullWidgetList.includes(fullWidgetName) : false;
  }, [metricName, fullWidgetName]);

  const fetchBreakdownData = useCallback(async () => {
    const shouldUsePointDateLoader =
      previousPointDate.current && previousPointDate.current !== pointDate;

    if (shouldUsePointDateLoader) {
      setIsPointDateLoading(true);
    } else {
      setIsLoading(true);
    }
    try {
      const selectedSiteParam = searchParams.get("selectedSite");
      const dimensionParam = searchParams.get("dimension");

      const type = selectedSiteParam
        ? "site"
        : dimensionParam || fetchBy === "organization"
        ? "dimension"
        : "site";

      const website_shared_uid = selectedSiteParam
        ? selectedSiteParam.split(",")
        : [Object.keys(userData?.data?.website_permissions?.[0] || {})[0]];

      let dimensions: string[] = [];

      if (dimensionParam) {
        dimensions = dimensionParam.split(",");
      } else if (fetchBy === "organization") {
        await TrendsDashboardEndPointLogic.getDimentions(oauthTokenHeader);
        const orgName = userData?.data?.user_details?.organization_name;
        const allOrgData = store.getState().trends.organizationOptions;
        const matched = allOrgData
          .filter(
            (org: any) => org.organization_id__organization_name === orgName
          )
          .map((org: any) => org.dimension);
        dispatch(
          setOrganizationDimensions(
            matched.map((uid: string) => ({ uid, siteName: uid }))
          )
        );
        dimensions = matched;
      } else {
        const defaultDimension = userData?.data?.dimensions?.[0];
        if (defaultDimension) dimensions = [defaultDimension];
      }

      const campaignId = searchParams.get("campaignId") || "1";
      const adgroupId = searchParams.get("adgroupId") || "1";
      const kwId = searchParams.get("kwId") || "1";
      const country = searchParams.get("country") || "all";
      const trafficSource = searchParams.get("trafficSource") || "all";

      const baseParams = {
        oauthTokenHeader,
        startDate: formattedDate,
        endDate: formattedDate,
        timeUnit: time_unit,
        type,
        websiteSharedUids: website_shared_uid,
        dimensions,
        campaignId,
        adgroupId,
        matchtype: "all",
        kwId,
        country,
        trafficSource,
      };

      const apiDefs = [
        {
          queryId: TrendsBreakdownQueryIds.COUNTRY,
          dispatchTo: setCountryDataTrendsBreakdown,
        },
        {
          queryId: TrendsBreakdownQueryIds.TRAFFIC_SOURCE,
          dispatchTo: setTrafficSourceDataTrendsBreakdown,
        },

        {
          queryId: TrendsBreakdownQueryIds.KEYWORD_MATCH_TYPE,
          dispatchTo: setKeywordMatchTypeDataTrendsBreakdown,
        },
        {
          queryId: TrendsBreakdownQueryIds.BRANDS,
          dispatchTo: setBrandsDataTrendsBreakdown,
        },
      ];

      const responses = await Promise.all(
        apiDefs.map(({ queryId }) =>
          trendsService.getTrendsBreakdownExecuteQuery({
            ...baseParams,
            // ...overrideParams,
            queryId,
          })
        )
      );

      responses.forEach((res, index) => {
        dispatch(apiDefs[index].dispatchTo(res));
      });
    } catch (e: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || e?.message || "Unknown error occurred",
          type: "error",
        })
      );
    } finally {
      if (shouldUsePointDateLoader) {
        // Add minimum loading time for better UX
        setTimeout(() => {
          setIsPointDateLoading(false);
        }, 500);
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [dispatch, userData, formattedDate, time_unit]);

  const formattedDateForMatch = useMemo(() => {
    if (!pointDate) return "";
    const [d, m, y] = pointDate.split("-");
    return `${y}-${m}-${d}`;
  }, [pointDate]);
  const dataSet: any = searchParams.get("dataSet");
  const keyMap: Record<string, Record<string, string>> = {
    action: {
      ftd: "ftds",
      clicks_out: "click_out",
      profit: "profit",
      spend: "spends",
      rs: "rev_share",
    },
    // add more dataset-specific mappings here if needed
  };
  // Compute pointValue from API data
  const pointValueFromData = useMemo(() => {
    if (!metricKey || !trendsOverviewDataforMetricValue?.query_output?.length)
      return null;
    const effectiveKey = keyMap[dataSet]?.[metricKey] ?? metricKey;
    const matchingRecord = trendsOverviewDataforMetricValue?.query_output.find(
      (item: any) => item.time_unit === formattedDateForMatch
    );

    return matchingRecord?.[effectiveKey] ?? null;
    // eslint-disable-next-line
  }, [
    metricKey,
    trendsOverviewDataforMetricValue,
    formattedDateForMatch,
    dataSet,
  ]);
  const [isMetricValLoading, setIsMetricValLoading] = useState(false);
  const formatToYYYYMMDD = (dateStr: string | null) => {
    if (!dateStr) return "";

    // Already in YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    // Detect separator
    const separator = dateStr.includes("-") ? "-" : "/";
    const parts = dateStr.split(separator);

    let year: string, month: string, day: string;

    if (parts.length === 3) {
      // Handle DD-MM-YYYY or YYYY-MM-DD
      if (parts[0].length === 4) {
        // Already YYYY-MM-DD style but wrong separator
        [year, month, day] = parts;
      } else {
        // Assume DD-MM-YYYY or DD/MM/YYYY
        [day, month, year] = parts;
      }
      // Ensure leading zeros
      month = month.padStart(2, "0");
      day = day.padStart(2, "0");

      // Return in strict YYYY-MM-DD without converting to Date()
      return `${year}-${month}-${day}`;
    }

    return "";
  };

  const fetchTrendsOverviewDataforMetricValue = useCallback(async () => {
    setIsMetricValLoading(true);
    try {
      const selectedSiteParam = searchParams.get("selectedSite");
      const dimensionParam = searchParams.get("dimension");
      const defaultDimension = searchParams.get("default_dimension");
      const defaultWebsiteUid = searchParams.get("default_website_uid");
      const startDateParam = formatToYYYYMMDD(
        searchParams.get("selectedStartDate")
      );
      const endDateParam = formatToYYYYMMDD(
        searchParams.get("selectedEndDate")
      );
      const dataSet = searchParams.get("dataSet");
      const queryId =
        dataSet === "click"
          ? TrendsDataSetQueryIds.CLICK
          : TrendsDataSetQueryIds.ACTION;
      const type = selectedSiteParam
        ? "site"
        : dimensionParam || fetchBy === "organization"
        ? "dimension"
        : "site";

      const website_shared_uid = selectedSiteParam
        ? selectedSiteParam.split(",")
        : [defaultWebsiteUid];

      let dimensions: string[] = [];

      if (dimensionParam) {
        dimensions = dimensionParam.split(",");
      } else if (fetchBy === "organization") {
        await TrendsDashboardEndPointLogic.getDimentions(oauthTokenHeader);
        const orgName = userData?.data?.user_details?.organization_name;
        const allOrgData = store.getState().trends.organizationOptions;
        const matched = allOrgData
          .filter(
            (org: any) => org.organization_id__organization_name === orgName
          )
          .map((org: any) => org.dimension);
        dispatch(
          setOrganizationDimensions(
            matched.map((uid: string) => ({ uid, siteName: uid }))
          )
        );
        dimensions = matched;
      } else {
        if (defaultDimension) dimensions = [defaultDimension];
      }

      const campaignId = searchParams.get("campaignId") || "1";
      const adgroupId = searchParams.get("adgroupId") || "1";
      const kwId = searchParams.get("kwId") || "1";
      const country = searchParams.get("country") || "all";
      const trafficSource = searchParams.get("trafficSource") || "all";

      const baseParams: any = {
        oauthTokenHeader,
        startDate: startDateParam
          ? startDateParam
          : dayjs(selectedStartDate).format("YYYY-MM-DD"),
        endDate: endDateParam
          ? endDateParam
          : dayjs(selectedEndDate).format("YYYY-MM-DD"),
        timeUnit: time_unit,
        type,
        websiteSharedUids: website_shared_uid,
        dimensions,
        campaignId,
        adgroupId,
        matchtype: "all",
        kwId,
        country,
        trafficSource,
      };
      const response = await trendsService.getTrendsDashboardExecuteQuery1({
        ...baseParams,
        queryId,
      });
      setTrendsOverviewDataforMetricValue(response.data.data || []);
    } catch (e: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || e?.message || "Unknown error occurred",
          type: "error",
        })
      );
    } finally {
      setIsMetricValLoading(false);
    }
    // eslint-disable-next-line
  }, [
    dispatch,
    userData,
    time_unit,
    // searchParams,
    fetchBy,
    oauthTokenHeader,
    selectedStartDate,
    selectedEndDate,
  ]);

  useEffect(() => {
    fetchBreakdownData();
  }, [fetchBreakdownData]);

  useEffect(() => {
    fetchTrendsOverviewDataforMetricValue();
  }, [fetchTrendsOverviewDataforMetricValue]);

  useEffect(() => {
    if (previousPointDate.current && previousPointDate.current !== pointDate) {
      setIsPointDateLoading(true);
    }
    previousPointDate.current = pointDate;
  }, [pointDate]);

  const isPercentageMetric = (key: string) => {
    return [
      "imp_share",
      "out_in",
      "qftd_ftd",
      "regs_out",
      "ftd_regs",
      "cvr",
      "average_revenue_share_percentage_deal",
    ].includes(key);
  };
  const [lastStableValue, setLastStableValue] = useState<number | null>(null);
  useEffect(() => {
    if (pointValueFromData !== null) {
      setLastStableValue(pointValueFromData);
    }
  }, [pointValueFromData]);
  return (
    <>
      {isPointDateLoading && <SimpleBackdrop />}
      {isLoading ? (
        <SimpleBackdrop />
      ) : (
        <>
          <>
            <Paper sx={{ padding: "8px 16px", mt: 1, marginBottom: "21px" }}>
              <Grid container spacing={2}>
                <Grid size={10}>
                  <Box>
                    <Stack
                      direction={"row"}
                      gap={2}
                      alignItems={"center"}
                      mb={2}
                    >
                      {!widgetName ? (
                        <Typography variant="h5">
                          {fullWidgetName} over time Breakdown
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          {widgetName?.toUpperCase()} ({fullWidgetName}) over
                          time Breakdown
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Grid>

                <Grid size={2}>
                  <Stack
                    direction={"row"}
                    gap={2}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <IconButton
                      onClick={() => changePointDate("previous")}
                      disabled={
                        isPointDateLoading ||
                        navigationLimits.isPreviousDisabled
                      }
                      sx={{
                        border: "2px solid #9C27B080", // purple border
                        color: navigationLimits.isPreviousDisabled
                          ? "#ccc"
                          : "#C266D3", // purple arrow or gray when disabled
                        width: 32,
                        height: 32,
                        "&:hover": {
                          backgroundColor: navigationLimits.isPreviousDisabled
                            ? "transparent"
                            : "rgba(194, 102, 211, 0.1)", // subtle hover effect
                        },
                        "&:disabled": {
                          borderColor: "#e0e0e0",
                          color: "#ccc",
                        },
                      }}
                      size="small"
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography fontSize={"22px"}>
                      {" "}
                      {pointDate || "N/A"}
                    </Typography>
                    <IconButton
                      onClick={() => changePointDate("next")}
                      disabled={
                        isPointDateLoading || navigationLimits.isNextDisabled
                      }
                      sx={{
                        border: "2px solid #9C27B080", // purple border
                        color: navigationLimits.isNextDisabled
                          ? "#ccc"
                          : "#C266D3", // purple arrow or gray when disabled
                        width: 32,
                        height: 32,
                        "&:hover": {
                          backgroundColor: navigationLimits.isNextDisabled
                            ? "transparent"
                            : "rgba(194, 102, 211, 0.1)", // subtle hover effect
                        },
                        "&:disabled": {
                          borderColor: "#e0e0e0",
                          color: "#ccc",
                        },
                      }}
                      size="small"
                    >
                      <KeyboardArrowRightOutlinedIcon />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: "400" }}>
                  Value on this date:{" "}
                  {isLoading || isPointDateLoading || isMetricValLoading ? (
                    // Instead of flickering Skeleton, show last known value
                    lastStableValue !== null ? (
                      `${(isPercentageMetric(metricKey)
                        ? lastStableValue * 100
                        : lastStableValue
                      ).toFixed(2)}${valueSuffixParams || ""}`
                    ) : (
                      <Skeleton
                        variant="text"
                        width={50}
                        height={24}
                        sx={{ display: "inline-block" }}
                      />
                    )
                  ) : pointValueFromData !== null ? (
                    `${(isPercentageMetric(metricKey)
                      ? pointValueFromData * 100
                      : pointValueFromData
                    ).toFixed(2)}${valueSuffixParams || ""}`
                  ) : (
                    <Skeleton
                      variant="text"
                      width={50}
                      height={24}
                      sx={{ display: "inline-block" }}
                    />
                  )}
                </Typography>

                {fetchBy === "site" ? (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {matchingSites?.map((siteName) => (
                      <Chip
                        key={siteName}
                        label={siteName}
                        sx={{
                          bgcolor: "secondary.dark",
                          color: "white",
                          height: "26px",
                          textTransform: "capitalize",
                        }}
                      />
                    ))}
                  </Stack>
                ) : fetchBy === "dimension" ? (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {dimensions?.map((dname) => (
                      <Chip
                        key={dname}
                        label={dname}
                        sx={{
                          bgcolor: "secondary.dark",
                          color: "white",
                          height: "26px",
                          textTransform: "capitalize",
                        }}
                      />
                    ))}
                  </Stack>
                ) : null}
              </Grid>
            </Paper>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TrendsBDTrafficSource />
              </Grid>

              <Grid size={6}>
                <TrendsBDCountry />{" "}
              </Grid>
            </Grid>

            <TrendsBDAds />
            <Box gap={2}>
              <Box>
                {" "}
                <TrendsBDKwMtTable />
              </Box>

              {showBrandsTable && (
                <Box mt={2}>
                  <TrendsBDBrandsTable />
                </Box>
              )}
            </Box>
          </>
          <div data-tracker="397">
            {fetchBy === "site" && selectedSiteParam?.length > 0 && (
              <FloatingActionItems
                selectedSiteFloatingAction={selectedSiteParam.split(",")[0]}
                selectedDimensionsFloatingAction=""
                trackIdFloatingAction="427"
              />
            )}

            {fetchBy === "dimension" && dimensionParam?.length > 0 && (
              <FloatingActionItems
                selectedSiteFloatingAction=""
                selectedDimensionsFloatingAction={dimensionParam.split(",")[0]}
                trackIdFloatingAction="427"
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default TrendsBreakdown;
